import { useState } from 'react';
import { useSearchParams, NavLink, Outlet, useLocation } from 'react-router-dom';
import DispatchResetButton from '../../components/DispatchResetButton';
import NukeFloorButton from '../../components/NukeFloorButton';
import { DEFAULT_COMPANY } from '../../constants/company';

// Plan — one surface for the three stages of getting work onto the floor.
//
//   Sandbox  — what COULD the plant do?   (author a demand scenario)
//   Flow     — how WILL the week go?      (plan routing + assignment)
//   Schedule — what IS running?           (live Gantt)
//
// Layout follows the Laws of UX rather than taste:
// - CHUNKING + PROXIMITY: identity ("Plan"), navigation (tabs) and the cross-tab action
//   (Reset dispatch) share ONE bar, so the page's chrome is a single perceptual group instead of
//   three stacked strips. This also reclaimed ~90px of vertical space — the Capacity panel used to
//   be cut off below the fold on a 900px viewport.
// - COGNITIVE LOAD: each tab's explanatory paragraph used to sit permanently on the page, re-read on
//   every visit and never again useful after the first. It now lives in a tooltip on the tab it
//   describes: available on demand, absent the rest of the time.
// - HICK'S LAW: three tabs, no overflow menu, no nested choices.
// - JAKOB'S LAW: underlined active tab, left-aligned after the title — the pattern every app uses.
// - FITTS'S LAW: Reset dispatch is pinned to the far right, deliberately far from the tabs, because
//   it is the only destructive control here and must not sit adjacent to routine navigation.
// - The "›" between tabs encodes a real ordering (scenario → plan → running). It is a hint, not a
//   wizard — every tab stays independently reachable.
const TABS = [
  { to: 'sandbox',  label: 'Sandbox',
    tip: 'Design a weekly demand scenario and test whether the plant can support it. Adjust the demand mix, station counts or cycle times to find a viable configuration — plant edits here are hypothetical.' },
  { to: 'flow',     label: 'Flow',
    tip: 'Commit to how the week will actually go. Plans the routing and machine assignment for the scenario, then simulates it so you can see where the week breaks before it runs.' },
  { to: 'schedule', label: 'Schedule',
    tip: "What is running right now — the live Gantt of dispatched work, refreshed every 10s. This is reality, not a projection." },
];

function Tab({ to, label, tip, qs, showChevron }) {
  const [hover, setHover] = useState(false);
  const tipId = `plan-tab-tip-${to}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      <NavLink
        to={{ pathname: to, search: qs }}
        aria-describedby={hover ? tipId : undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}          // keyboard users get the tooltip too
        onBlur={() => setHover(false)}
        style={({ isActive }) => ({
          padding: '6px 2px',
          fontSize: 13.5,
          fontWeight: isActive ? 800 : 600,
          color: isActive ? 'var(--cn-text)' : 'var(--cn-muted)',
          borderBottom: `2px solid ${isActive ? 'var(--cn-gold)' : 'transparent'}`,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          transition: 'color 0.15s',
        })}
      >
        {label}
      </NavLink>

      {showChevron && (
        <span aria-hidden="true" style={{ color: 'var(--cn-dimmed)', opacity: 0.5, margin: '0 12px', fontSize: 12 }}>›</span>
      )}

      {hover && (
        <div
          id={tipId}
          role="tooltip"
          style={{
            position: 'absolute', top: 'calc(100% + 10px)', left: 0, zIndex: 60,
            width: 300, padding: '10px 12px',
            background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.28)',
            borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
            fontSize: 12.5, lineHeight: 1.5, color: 'var(--cn-muted)', fontWeight: 500,
            pointerEvents: 'none',
          }}
        >
          {tip}
        </div>
      )}
    </div>
  );
}

export default function PlanPage() {
  const [searchParams] = useSearchParams();
  const companySlug    = searchParams.get('company') || DEFAULT_COMPANY;
  const location       = useLocation();
  const qs             = location.search;   // keep ?company= when moving between tabs

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* ONE bar: identity + navigation + the action that spans all three tabs. No subheader —
          the company already appears in the sidebar selector, and repeating it here was the third
          place on screen saying "rassini". */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap',
        padding: '14px 26px 0', flexShrink: 0,
      }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--cn-text)', margin: 0, letterSpacing: '0.01em' }}>
          Plan
        </h1>

        <nav style={{ display: 'flex', alignItems: 'center' }}>
          {TABS.map((t, i) => (
            <Tab key={t.to} {...t} qs={qs} showChevron={i < TABS.length - 1} />
          ))}
        </nav>

        {/* Reset dispatch (safe: never touches started work) then Nuke (cancels it). Ordered
            least-to-most destructive, with a divider so the two don't read as a pair of equals. */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <DispatchResetButton companySlug={companySlug} />
          <div style={{ width: 1, height: 20, background: 'rgba(168,144,96,0.22)' }} />
          <NukeFloorButton companySlug={companySlug} />
        </div>
      </div>
      <div style={{ height: 1, background: 'rgba(168,144,96,0.16)', margin: '0 26px 4px', flexShrink: 0 }} />

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Outlet />
      </div>
    </div>
  );
}
